import { type GenerateAnswerByAnswersArticlesAtomsStreamedResponse as StreamingResult } from '@wix/ambassador-cc-helpcenter-v1-generated-answer/types';

export const fetchRefinementQuestions = async (
  searchTerm: string,
  abortController?: AbortController
): Promise<string[] | null> => {
  if (process.env.NODE_ENV === 'development') {
    const allQuestions = [
      'How do I customize my homepage?',
      'What are the best practices for SEO?',
      'How can I integrate third-party tools?',
      'How do I set up a payment gateway?',
      'What are the mobile optimization options?',
      'How do I add a contact form?',
      'What payment methods are supported?',
      'How do I improve site speed?',
      'How do I add custom CSS?',
      'What are the best practices for accessibility?',
    ];

    const shuffled = allQuestions.sort(() => Math.random() - 0.5);
    const count = Math.floor(Math.random() * 3) + 3; // Random 3-5
    return shuffled.slice(0, count);
  }

  try {
    const endpoint =
      '/_serverless/cc-user-facing-service/generate-answer/refinement-questions';
    const url = `${endpoint}?searchTerm=${encodeURIComponent(searchTerm)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-wix-client-artifact-id': 'kb-client-nextjs',
      },
      signal: abortController?.signal,
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data ?? null;
  } catch (_) {
    return null;
  }
};

export const streamGenerateAnswer = async (
  body: {
    searchTerm: string;
    language: string;
    editorType?: string | string[] | undefined;
    agPromptId?: string;
  },
  timeBudgetMs: number,
  onUpdate?: (partial: Partial<StreamingResult>) => void,
  abortController?: AbortController
): Promise<null> => {
  const controller = abortController || new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeBudgetMs);
  try {
    const endpoint =
      process.env.NODE_ENV === 'development'
        ? '/api/mock-generate-answer'
        : '/_serverless/cc-user-facing-service/generate-answer/streamed';

    const response: Response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        'x-wix-client-artifact-id': 'kb-client-nextjs',
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response?.ok || !response.body) {
      return null;
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let result = await reader.read();
    while (!result.done) {
      const { value } = result;
      buffer += decoder.decode(value, { stream: true });
      buffer = processLines(buffer, onUpdate);
      result = await reader.read();
    }
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
};

const processLines = (
  buffer: string,
  onUpdate?: (partial: Partial<StreamingResult>) => void
): string => {
  let i = buffer.indexOf('\n');
  while (i !== -1) {
    const line = buffer.slice(0, i).trim();
    buffer = buffer.slice(i + 1);
    if (line) {
      try {
        const partial = JSON.parse(line) as Partial<StreamingResult>;
        onUpdate?.(partial);
      } catch (_) {
        // ignore malformed chunk
      }
    }
    i = buffer.indexOf('\n');
  }
  return buffer;
};
